import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { testDatabaseConnection } from '../services/api';

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {React.cloneElement(icon, { sx: { fontSize: 40, color } })}
        <Typography variant="h6" sx={{ ml: 2 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" component="div">
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const ProjectProgress = ({ title, progress, color }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="body1">{title}</Typography>
      <Typography variant="body2">{progress}%</Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={progress}
      sx={{
        height: 8,
        borderRadius: 5,
        backgroundColor: `${color}22`,
        '& .MuiLinearProgress-bar': {
          backgroundColor: color,
          borderRadius: 5,
        },
      }}
    />
  </Box>
);

const Dashboard = () => {
  const [dbStatus, setDbStatus] = useState({ success: false, message: '' });

  useEffect(() => {
    const checkDatabaseConnection = async () => {
      try {
        const result = await testDatabaseConnection();
        setDbStatus({
          success: result.success,
          message: 'Database connection successful!',
        });
      } catch (error) {
        setDbStatus({
          success: false,
          message: 'Failed to connect to database',
        });
      }
    };

    checkDatabaseConnection();
  }, []);

  // Sample data - in a real app, this would come from your backend
  const stats = [
    {
      title: 'Total Projects',
      value: '12',
      icon: <AssignmentIcon />,
      color: '#1976d2',
    },
    {
      title: 'Active Clients',
      value: '8',
      icon: <BusinessIcon />,
      color: '#2e7d32',
    },
    {
      title: 'Team Members',
      value: '24',
      icon: <PeopleIcon />,
      color: '#ed6c02',
    },
    {
      title: 'Completed Projects',
      value: '45',
      icon: <TimelineIcon />,
      color: '#9c27b0',
    },
  ];

  const projects = [
    { title: 'Website Redesign', progress: 75, color: '#1976d2' },
    { title: 'Mobile App Development', progress: 45, color: '#2e7d32' },
    { title: 'Cloud Migration', progress: 90, color: '#ed6c02' },
    { title: 'Data Analytics Platform', progress: 30, color: '#9c27b0' },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Dashboard
      </Typography>

      {dbStatus.message && (
        <Alert
          severity={dbStatus.success ? 'success' : 'error'}
          sx={{ mb: 3 }}
        >
          {dbStatus.message}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Project Progress
            </Typography>
            {projects.map((project, index) => (
              <ProjectProgress key={index} {...project} />
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Recent Activities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • New project 'Data Analytics Platform' created
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Team meeting scheduled for Website Redesign
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Cloud Migration project reached 90% completion
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • New team member added to Mobile App project
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;